import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { appendToRmaBaseUrl } from '@rma/generic/ng/util';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { PublicProfileContextService, PublicUserContextService } from '@rma/public/accounts/user';
import { QrCodeService } from '@rma/public/generic/api/qr-code';
import { ErrorService } from '@rma/public/generic/fallback-pages';
import { PlatformService } from '@rma/public/generic/util/environment';
import { catchError, combineLatest, filter, map, Observable, of, switchMap, take } from 'rxjs';
import { MarketReportWithQrCode } from '../domain/market-report';
import { MarketReportApiService } from './market-report-api/market-report-api.service';

export const marketReportResolver = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<MarketReportWithQrCode> => {
  const currentUserService = inject(PublicUserContextService);
  const profileContext = inject(PublicProfileContextService);
  const errorService = inject(ErrorService);
  const qrCodeService = inject(QrCodeService);
  const rmaBaseUrl = inject(appendToRmaBaseUrl);
  const service = inject(MarketReportApiService);

  const platformService = inject(PlatformService);

  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const marketCode = route.queryParamMap.get('code') as string;
  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const isPrintMode = route.queryParamMap.get('print') as string;

  const currentUserProfileContext$ = platformService.isPlatformBrowser
    ? combineLatest([currentUserService.currentUser$, profileContext.nullableProfileIdentifier$]).pipe(
        // An authenticated non-Consumer is expected to have a resolvable profile, so wait past the transient null a bad
        // pointer emits before the fallback recovers - otherwise the report would resolve unscoped. For anyone else (no
        // user, or a Consumer) null is the settled answer and is used as-is.
        filter(([user, profile]) => profile !== null || !user || user.userType === 'Consumer'),
        map(([, profile]) => profile),
      )
    : of(null);

  return currentUserProfileContext$.pipe(
    take(1),
    switchMap((profile) => service.getReport(marketCode, isPrintMode ?? 'false', profile?.profileType, profile?.profileCode)),
    switchMap((report) => {
      const utm = `utm_source=market-report&utm_medium=qr_code&utm_campaign=${report.listingReportCode}`;
      const url = report.profile.profileUrl;
      return qrCodeService.getQrCodeForText(rmaBaseUrl(`${url}?${utm}`)).pipe(map((qrCodeDataUri) => ({ ...report, qrCodeDataUri })));
    }),
    rmaShareReplay(),
    catchError((e) => errorService.handleError<MarketReportWithQrCode>(state.url, e)),
  );
};
